import { useState } from 'react';

import { apiErrorMessage } from '@shared/api/errors';
import { useUserOptionsQuery } from '@shared/api/lookupsApi';
import type { RoleDefaults, StepRole } from '@shared/api/types';
import {
  BaseButton,
  BaseDialog,
  BaseField,
  BaseSelect,
  BaseSpinner,
  useToast,
} from '@shared/components/base';
import { COMMON_TEXT } from '@shared/constants/text';

import { DASHBOARD_TEXT } from '../constants';
import { ROLE_LABEL, TEAM_ROLES, assignable } from '../lib/board';

type Roles = Partial<Record<StepRole, string | null>>;

/** Default holder of each role. New Income Tax Return tasks start with this team
 *  (a client's own team, when set, wins over the firm's). */
export function TeamEditor({
  idPrefix,
  value,
  onChange,
  disabled,
}: {
  idPrefix: string;
  value: Roles;
  onChange: (roles: Roles) => void;
  disabled?: boolean;
}) {
  const { data: users } = useUserOptionsQuery(undefined);
  const options = assignable(users).map((u) => ({ value: u.id, label: u.fullName }));
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {TEAM_ROLES.map((role) => (
        <BaseField key={role} id={`${idPrefix}-${role}`} label={ROLE_LABEL[role]}>
          <BaseSelect<string>
            id={`${idPrefix}-${role}`}
            value={value[role] ?? null}
            options={options}
            placeholder={DASHBOARD_TEXT.team.nobody}
            clearable
            searchable
            disabled={disabled}
            aria-label={ROLE_LABEL[role]}
            onChange={(v) => {
              onChange({ ...value, [role]: v });
            }}
          />
        </BaseField>
      ))}
    </div>
  );
}

export function TeamDialog({
  visible,
  onHide,
  title,
  hint,
  initial,
  loading,
  onSave,
}: {
  visible: boolean;
  onHide: () => void;
  title: string;
  hint: string;
  initial: RoleDefaults | undefined;
  loading: boolean;
  onSave: (body: RoleDefaults) => Promise<{ error?: unknown }>;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState<Roles | null>(null);
  const value: Roles = draft ?? initial?.roles ?? {};
  const close = () => {
    setDraft(null);
    onHide();
  };
  return (
    <BaseDialog
      visible={visible}
      onHide={close}
      header={title}
      width="md"
      footer={
        <>
          <BaseButton variant="secondary" onClick={close}>
            {COMMON_TEXT.actions.cancel}
          </BaseButton>
          <BaseButton
            loading={loading}
            disabled={draft === null}
            onClick={() => {
              void (async () => {
                const res = await onSave({ roles: value });
                if (res.error) {
                  toast.error(apiErrorMessage(res.error));
                  return;
                }
                toast.success(DASHBOARD_TEXT.team.saved);
                close();
              })();
            }}
          >
            {COMMON_TEXT.actions.save}
          </BaseButton>
        </>
      }
    >
      {initial ? (
        <div className="flex flex-col gap-3">
          <p className="text-[12.5px] text-text-2 normal-case">{hint}</p>
          <TeamEditor idPrefix="team" value={value} onChange={setDraft} />
        </div>
      ) : (
        <BaseSpinner />
      )}
    </BaseDialog>
  );
}
